import React, { useState, useEffect } from 'react';

const CARDS = (high, dual, snapshotCount, todayCount, precisionValue, precisionSub) => [
  {
    label:      'Tracks monitored',
    shortLabel: 'Tracks',
    value:      '50',
    sub:        'Global Top 50',
    color:      'teal',
    icon: (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
      </svg>
    ),
  },
  {
    label:      'Alerts today',
    shortLabel: 'Alerts today',
    value:      todayCount !== null && todayCount > 0 ? String(todayCount) : '—',
    sub:        todayCount !== null && todayCount > 0 ? 'Flagged today' : 'None today',
    color:      'teal',
    icon: (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="var(--accent)" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
        <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
      </svg>
    ),
  },
  {
    label:      'Days of data',
    shortLabel: 'Days of data',
    value:      snapshotCount !== null ? String(snapshotCount) : '—',
    sub:        'Days of history',
    color:      'teal',
    icon: (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <ellipse cx="12" cy="5" rx="9" ry="3"/>
        <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
        <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
      </svg>
    ),
  },
  {
    label:      'High confidence',
    shortLabel: 'High confidence',
    value:      String(high),
    sub:        'Last 7 days',
    color:      high > 0 ? 'amber' : 'teal',
    icon: (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={high > 0 ? 'var(--amber)' : 'var(--accent)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
    ),
  },
  {
    label:      'Dual-model alerts',
    shortLabel: 'Dual-model',
    value:      String(dual),
    sub:        'Last 7 days',
    color:      dual > 0 ? 'amber' : 'teal',
    icon: (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={dual > 0 ? 'var(--amber)' : 'var(--accent)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
      </svg>
    ),
  },
  {
    label:      'Model precision',
    shortLabel: 'Precision',
    value:      precisionValue,
    sub:        precisionSub,
    color:      'purple',
    icon: (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--purple)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10"/>
        <line x1="12" y1="20" x2="12" y2="4"/>
        <line x1="6" y1="20" x2="6" y2="14"/>
      </svg>
    ),
  },
];

const BADGE_BG   = { teal: 'var(--accent-bg)', amber: 'var(--amber-bg)', purple: 'var(--purple-bg)' };
const VALUE_COLOR = { teal: 'var(--text)', amber: 'var(--amber)', purple: 'var(--purple)' };
const SUB_COLOR   = { teal: 'var(--accent)', amber: 'var(--amber)', purple: 'var(--muted)' };

export default function MetricCards({ alertsData, allAlerts = [], snapshotCount, todayCount }) {
  const high = alertsData ? alertsData.filter(a => a.severity === 'high').length : 0;
  const dual = alertsData ? alertsData.filter(a => a.both_models === true).length : 0;

  const closedAlerts = allAlerts.filter(a => {
    if (!a.detected_date) return false;
    if (!a.outcome || a.outcome === 'pending') return false;
    const detected = new Date(a.detected_date + 'T00:00:00');
    const now = new Date();
    const daysElapsed = (now - detected) / (1000 * 60 * 60 * 24);
    if (daysElapsed < 7) return false;
    const vsRolling = parseFloat(a.playcount_vs_rolling);
    const score = parseFloat(a.anomaly_score);
    if (!isNaN(vsRolling) && vsRolling > 1.05) return false;
    if (!isNaN(score) && score < 0.45) return false;
    return true;
  });


  const correctPredictions = closedAlerts.filter(a =>
    a.outcome === 'went_viral' || a.outcome === 'partial_rise'
  );

  const precision = closedAlerts.length > 0
    ? Math.round((correctPredictions.length / closedAlerts.length) * 100)
    : null;

  const precisionValue = precision !== null ? String(precision) + '%' : '—';
  const precisionSub = closedAlerts.length > 0
    ? `${closedAlerts.length} closed alerts`
    : 'Awaiting closed alerts';

  const cards = CARDS(high, dual, snapshotCount, todayCount, precisionValue, precisionSub);

  const [displayed, setDisplayed] = useState(cards.map(() => 0));
  const [visible, setVisible] = useState(cards.map(() => false));

  useEffect(() => {
    const currentCards = CARDS(high, dual, snapshotCount, todayCount, precisionValue, precisionSub);

    currentCards.forEach((card, i) => {
      setTimeout(() => {
        setVisible(prev => {
          const next = [...prev];
          next[i] = true;
          return next;
        });
      }, i * 100);

      const raw = card.value;
      if (raw === '—' || raw === null || raw === undefined) {
        setDisplayed(prev => {
          const next = [...prev];
          next[i] = 0;
          return next;
        });
        return;
      }
      const target = parseFloat(raw);
      if (isNaN(target)) return;

      const duration = 600;
      const startTime = Date.now() + i * 100;

      function tick() {
        const now = Date.now();
        if (now < startTime) { requestAnimationFrame(tick); return; }
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        const current = Math.round(target * eased);
        setDisplayed(prev => {
          const next = [...prev];
          next[i] = current;
          return next;
        });
        if (progress < 1) requestAnimationFrame(tick);
      }
      requestAnimationFrame(tick);
    });
  }, [snapshotCount, todayCount, alertsData, allAlerts]);

  function getDisplayValue(card, i) {
    const raw = card.value;
    if (raw === '—') return '—';
    const target = parseFloat(raw);
    if (isNaN(target)) return raw;
    return raw.includes('%') ? `${displayed[i]}%` : String(displayed[i]);
  }

  return (
    <div className="metric-cards-grid" style={{
      gap: '1px',
      background: 'var(--border)',
      borderBottom: '1px solid var(--border)'
    }}>
      {cards.map((c, i) => (
        <div key={i} style={{
          background: 'var(--surface)',
          padding: '12px 16px',
          boxShadow: 'var(--card-shadow)',
          borderRadius: '0',
          opacity: visible[i] ? 1 : 0,
          transform: visible[i] ? 'translateY(0)' : 'translateY(6px)',
          transition: 'opacity 0.4s ease, transform 0.4s ease'
        }}>
          {/* Icon badge + label row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
            <div style={{
              width: '24px', height: '24px', borderRadius: '6px',
              background: BADGE_BG[c.color],
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0
            }}>
              {c.icon}
            </div>
            <div style={{
              fontSize: '10px', color: 'var(--muted)',
              textTransform: 'uppercase', letterSpacing: '0.05em',
            }}>
              <span className="metric-label-full">{c.label}</span>
              <span className="metric-label-short" style={{ display: 'none' }}>{c.shortLabel}</span>
            </div>
          </div>

          {/* Value */}
          <div style={{
            fontSize: '26px', fontWeight: '600', letterSpacing: '-0.5px',
            color: VALUE_COLOR[c.color], lineHeight: 1, marginBottom: '3px'
          }}>
            {getDisplayValue(c, i)}
          </div>

          {/* Sub */}
          <div style={{ fontSize: '10px', color: SUB_COLOR[c.color] }}>
            {c.sub}
          </div>
        </div>
      ))}
    </div>
  );
}